import type {ColumnDef} from '@tanstack/react-table';

import tableSelectCol from '@/components/data-table/cols/tableSelectCol';
import {Avatar, AvatarFallback, AvatarImage} from '@/components/ui/avatar';
import i18n from '@/i18n';

import type {ResidentWithOccupiedRoom} from './getResidentsWithRoom';
import {sortDateColumn} from './sortDateColumn';

const residentIdCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('ID'),
  id: 'id',
  accessorKey: 'id',
  enableSorting: false,
};

const residentPersonalNumberCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('Personal number'),
  id: 'personalNumber',
  accessorKey: 'personalNumber',
  enableSorting: false,
};

const residentStatusCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('Status'),
  id: 'status',
  accessorKey: 'status',
  enableSorting: true,
};

const residentAvatarCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('Avatar'),
  accessorKey: 'avatar',
  id: 'avatar',
  enableHiding: false,
  enableSorting: false,
  cell: ({row}) => {
    const avatar = row.getValue<string>('avatar');
    const firstName = row.getValue<string>('firstName');
    const lastName = row.getValue<string>('lastName');

    return (
      <Avatar>
        {avatar && (
          <AvatarImage src={avatar} alt={`${firstName} ${lastName}`} />
        )}
        <AvatarFallback>{firstName[0] + lastName[0]}</AvatarFallback>
      </Avatar>
    );
  },
};

const residentFirstNameCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('First name'),
  accessorKey: 'name.firstName',
  id: 'firstName',
};

const residentMiddleNameCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('Middle name'),
  accessorKey: 'name.middleName',
  id: 'middleName',
};

const residentLastNameCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('Last name'),
  accessorKey: 'name.lastName',
  id: 'lastName',
};

const residentOccupiedRoomCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('Occupied room'),
  accessorKey: 'occupiedRoom',
  id: 'occupiedRoom',
};

const residentDateOfBirthCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('Date of birth'),
  sortingFn: sortDateColumn,
  accessorKey: 'dateOfBirth',
  id: 'dateOfBirth',
};

const residentAccommodatedSinceCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('Accommodated since'),
  sortingFn: sortDateColumn,
  accessorKey: 'accommodatedSince',
  id: 'accommodatedSince',
};

const residentAccommodatedUntilCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('Accommodated until'),
  sortingFn: sortDateColumn,
  accessorKey: 'accommodatedUntil',
  id: 'accommodatedUntil',
};

const residentContactPhoneNumberCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('Phone'),
  id: 'contactPhoneNumber',
  enableSorting: false,
  accessorFn: resident => resident.contactPersons[0]?.phoneNumber,
};

const residentContactEmailCol: ColumnDef<ResidentWithOccupiedRoom> = {
  header: i18n.t('Email'),
  id: 'contactEmail',
  enableSorting: false,
  accessorFn: resident => resident.contactPersons[0]?.email,
};

export const residentColumns: ColumnDef<ResidentWithOccupiedRoom>[] = [
  tableSelectCol<ResidentWithOccupiedRoom>(),
  residentIdCol,
  residentPersonalNumberCol,
  residentStatusCol,
  residentAvatarCol,
  residentFirstNameCol,
  residentMiddleNameCol,
  residentLastNameCol,
  residentOccupiedRoomCol,
  residentDateOfBirthCol,
  residentAccommodatedSinceCol,
  residentAccommodatedUntilCol,
  residentContactPhoneNumberCol,
  residentContactEmailCol,
];

export const exportResidentColumns: ColumnDef<ResidentWithOccupiedRoom>[] = [
  tableSelectCol<ResidentWithOccupiedRoom>(),
  residentAvatarCol,
  {
    ...residentIdCol,
    meta: {
      enableExport: false,
      enableExportPreviewToogle: false,
    },
  },
  {
    ...residentPersonalNumberCol,
    meta: {
      enableExport: true,
      enableExportPreviewToogle: true,
    },
  },
  {
    ...residentFirstNameCol,
    meta: {
      enableExport: true,
      enableExportPreviewToogle: true,
    },
  },
  {
    ...residentMiddleNameCol,
    meta: {
      enableExport: true,
      enableExportPreviewToogle: true,
    },
  },
  {
    ...residentLastNameCol,
    meta: {
      enableExport: true,
      enableExportPreviewToogle: true,
    },
  },
  {
    ...residentOccupiedRoomCol,
    meta: {
      enableExport: true,
      enableExportPreviewToogle: true,
    },
  },
  {
    ...residentDateOfBirthCol,
    meta: {
      enableExport: true,
      enableExportPreviewToogle: true,
    },
  },
  {
    ...residentAccommodatedSinceCol,
    meta: {
      enableExport: true,
      enableExportPreviewToogle: true,
    },
  },
  {
    ...residentAccommodatedUntilCol,
    meta: {
      enableExport: true,
      enableExportPreviewToogle: true,
    },
  },
  {
    ...residentContactPhoneNumberCol,
    meta: {
      enableExport: true,
      enableExportPreviewToogle: true,
    },
  },
  {
    ...residentContactEmailCol,
    meta: {
      enableExport: true,
      enableExportPreviewToogle: true,
    },
  },
];
